import { Fragment, useEffect, useId, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { ChevronDown, ChevronRight, ChevronUp, GripVertical, Lock, Plus, X } from "lucide-react";
import { useStore } from "../lib/store";
import type { Funnel } from "../lib/types";
import { Button, ColorMenu, Drawer, Field, TextArea, TextInput } from "../ui";
import {
  DEFAULT_STAGE_NAMES,
  STAGE_TEMPLATES,
  normalizeStageNames,
  stageTone,
} from "../features/funnels/hub";

/**
 * Novo funil / Editar funil e etapas — o diálogo `.cx-funnel-form` do protótipo:
 * identidade (nome, descrição) e a esteira de etapas editável em linhas, com
 * modelos e prévia. Criar grava funil e etapas numa operação do store; editar
 * grava nome/descrição e a esteira (renomear, criar, reordenar, arquivar).
 * Etapas com cadastros posicionados não saem por aqui (§7.3, §18.1).
 */

interface StageRow {
  key: string;
  id?: string;
  name: string;
}

export interface FunilFormProps {
  open: boolean;
  onClose: () => void;
  /** Funil em edição; ausente cria um novo. */
  funnel?: Funnel;
  onSaved?: (funnelId: string) => void;
}

let rowSeq = 0;
const rowKey = () => `row-${++rowSeq}`;

export function FunilForm({ open, onClose, funnel, onSaved }: FunilFormProps) {
  const store = useStore();
  const formId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const editing = Boolean(funnel);

  const existingStages = useMemo(
    () =>
      funnel
        ? store.stages
            .filter((stage) => stage.funnelId === funnel.id && !stage.archived)
            .sort((a, b) => a.order - b.order)
        : [],
    [store.stages, funnel],
  );
  const occupied = useMemo(() => {
    const ids = new Set<string>();
    for (const membership of store.memberships) if (membership.funnelId === funnel?.id) ids.add(membership.stageId);
    return ids;
  }, [store.memberships, funnel?.id]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rows, setRows] = useState<StageRow[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [newStage, setNewStage] = useState("");
  const [errors, setErrors] = useState<{ name?: string; stages?: string; form?: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(funnel?.name ?? "");
    setDescription(funnel?.description ?? "");
    setRows(
      funnel
        ? existingStages.map((stage) => ({ key: rowKey(), id: stage.id, name: stage.name }))
        : DEFAULT_STAGE_NAMES.map((stageName) => ({ key: rowKey(), name: stageName })),
    );
    setRemovedIds([]);
    setNewStage("");
    setErrors({});
    setDirty(false);
    setSubmitting(false);
    // Só no momento de abrir: o formulário não deve reagir a mudanças do store enquanto o usuário edita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, funnel?.id]);

  const touch = () => setDirty(true);

  const addRow = () => {
    const clean = newStage.trim().replace(/\s+/g, " ");
    if (!clean) return;
    if (rows.some((row) => row.name.toLocaleLowerCase("pt-BR") === clean.toLocaleLowerCase("pt-BR"))) {
      setErrors((current) => ({ ...current, stages: "Já existe uma etapa com este nome." }));
      return;
    }
    setRows((current) => [...current, { key: rowKey(), name: clean }]);
    setNewStage("");
    setErrors((current) => ({ ...current, stages: undefined }));
    touch();
  };

  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    setRows((current) => {
      const next = [...current];
      const [row] = next.splice(index, 1);
      next.splice(target, 0, row);
      return next;
    });
    touch();
  };

  const remove = (index: number) => {
    const row = rows[index];
    if (row.id && occupied.has(row.id)) return;
    setRows((current) => current.filter((_, i) => i !== index));
    if (row.id) setRemovedIds((current) => [...current, row.id!]);
    touch();
  };

  const applyTemplate = (templateId: string) => {
    const template = STAGE_TEMPLATES.find((item) => item.id === templateId);
    if (!template || editing) return;
    setRows(template.stages.map((stageName) => ({ key: rowKey(), name: stageName })));
    touch();
  };

  const onNewStageKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addRow();
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    const cleanName = name.trim().replace(/\s+/g, " ");
    const names = normalizeStageNames(rows.map((row) => row.name));
    const nextErrors: typeof errors = {};
    if (!cleanName) nextErrors.name = "Informe o nome do funil.";
    if (names.length === 0) nextErrors.stages = "Adicione ao menos uma etapa.";
    if (names.length !== rows.filter((row) => row.name.trim()).length) nextErrors.stages = "Duas etapas não podem ter o mesmo nome.";
    setErrors(nextErrors);
    if (nextErrors.name) {
      nameRef.current?.focus();
      return;
    }
    if (nextErrors.stages) return;
    setSubmitting(true);
    try {
      if (funnel) {
        const savedFunnel =
          cleanName === funnel.name && description.trim() === funnel.description.trim()
            ? true
            : await store.updateFunnel(funnel.id, { name: cleanName, description });
        if (!savedFunnel) throw new Error("funil");
        const savedStages = await store.saveFunnelStages(funnel.id, {
          rows: rows.filter((row) => row.name.trim()).map((row) => ({ id: row.id, name: row.name })),
          removedIds,
        });
        if (!savedStages) throw new Error("etapas");
        setDirty(false);
        onSaved?.(funnel.id);
        onClose();
        return;
      }
      const id = await store.createFunnel({ name: cleanName, description, stages: names });
      if (!id) throw new Error("funil");
      setDirty(false);
      onSaved?.(id);
      onClose();
    } catch {
      setErrors((current) => ({
        ...current,
        form: "Não foi possível salvar. Verifique a conexão e tente novamente; nada foi alterado.",
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const locked = rows.some((row) => row.id && occupied.has(row.id));
  const count = rows.filter((row) => row.name.trim()).length;
  const countLabel = `${count} ${count === 1 ? "etapa" : "etapas"}`;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? "Editar funil e etapas" : "Novo funil"}
      dirty={dirty}
      initialFocusRef={nameRef}
      className="cx-funnel-form"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Cancelar
          </button>
          <Button variant="primary" type="submit" form={formId} loading={submitting} disabled={submitting}>
            {editing ? "Salvar alterações" : "Criar rascunho"}
          </Button>
        </>
      }
    >
      <form id={formId} noValidate onSubmit={submit}>
        <p className="cx-form-caption">
          Defina as etapas comuns aos cadastros deste funil. As ações do processo são configuradas no editor.
        </p>
        <div className="fgrid">
          <div className="cx-form-section">Identidade</div>
          <Field label="Nome do funil" required error={errors.name}>
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                ref={nameRef}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                value={name}
                maxLength={120}
                autoComplete="off"
                onChange={(event) => {
                  setName(event.target.value);
                  touch();
                }}
              />
            )}
          </Field>
          <Field label="Descrição">
            {({ id, describedBy }) => (
              <TextArea
                id={id}
                aria-describedby={describedBy}
                value={description}
                rows={2}
                maxLength={400}
                onChange={(event) => {
                  setDescription(event.target.value);
                  touch();
                }}
              />
            )}
          </Field>
          <div className="cx-stage-editor">
            <div className="cx-form-section">
              Esteira de etapas<span className="num">{countLabel}</span>
            </div>
            <ol className="cx-stage-rows" aria-label="Etapas do funil">
              {rows.map((row, index) => {
                const isLocked = Boolean(row.id && occupied.has(row.id));
                return (
                  <li key={row.key} style={{ "--cx-tone": stageTone(index) } as React.CSSProperties}>
                    <span className="cx-stage-grip" aria-hidden="true">
                      <GripVertical />
                    </span>
                    <i className="cx-stage-n">{index + 1}</i>
                    <input
                      type="text"
                      value={row.name}
                      aria-label={`Nome da etapa ${index + 1}`}
                      maxLength={40}
                      autoComplete="off"
                      onChange={(event) => {
                        const value = event.target.value;
                        setRows((current) => current.map((item) => (item.key === row.key ? { ...item, name: value } : item)));
                        touch();
                      }}
                    />
                    <span className="cx-stage-row-actions">
                      {/* Cor é atributo da etapa gravada e vale na hora: ela
                          pinta a jornada, a Inbox e o Início. Etapa ainda não
                          criada não tem o que pintar (§18.1). */}
                      {row.id && (
                        <ColorMenu
                          about={`etapa ${row.name || index + 1}`}
                          value={store.stages.find((stage) => stage.id === row.id)?.color}
                          onChange={(color) => void store.updateConfigColor("stage", row.id!, color)}
                        />
                      )}
                      <button type="button" aria-label="Subir etapa" title="Subir" disabled={index === 0} onClick={() => move(index, -1)}>
                        <ChevronUp />
                      </button>
                      <button
                        type="button"
                        aria-label="Descer etapa"
                        title="Descer"
                        disabled={index === rows.length - 1}
                        onClick={() => move(index, 1)}
                      >
                        <ChevronDown />
                      </button>
                      <button
                        type="button"
                        className="is-remove"
                        aria-label="Remover etapa"
                        title={isLocked ? "Etapa com cadastros posicionados: mova-os antes de remover." : "Remover"}
                        disabled={isLocked}
                        onClick={() => remove(index)}
                      >
                        <X />
                      </button>
                    </span>
                  </li>
                );
              })}
            </ol>
            <div className="cx-stage-add">
              <TextInput
                value={newStage}
                placeholder="Nova etapa… (Enter adiciona)"
                aria-label="Nova etapa"
                maxLength={40}
                autoComplete="off"
                onChange={(event) => setNewStage(event.target.value)}
                onKeyDown={onNewStageKey}
              />
              <button type="button" className="btn-ghost" onClick={addRow}>
                <Plus />
                Adicionar
              </button>
            </div>
            <div className="cx-stage-templates">
              <span>Modelos</span>
              {STAGE_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  type="button"
                  disabled={editing}
                  title={editing ? "Modelos só substituem a esteira na criação do funil." : undefined}
                  onClick={() => applyTemplate(template.id)}
                >
                  {template.label}
                </button>
              ))}
            </div>
            {locked && (
              <p className="cx-hint is-lock">
                <Lock aria-hidden="true" />
                Etapas com cadastros posicionados continuam na esteira: mova os cadastros antes de removê-las. O histórico
                das visitas é preservado.
              </p>
            )}
            {errors.stages && (
              <p className="cx-hint is-warn" role="alert">
                {errors.stages}
              </p>
            )}
            <div className="cx-stage-preview">
              <div className="cx-stage-preview-label">
                <span>Prévia da esteira</span>
                <span className="num">{countLabel}</span>
              </div>
              <div className="cx-stage-chips">
                {rows
                  .filter((row) => row.name.trim())
                  .map((row, index, list) => (
                    // Fragmento, não `display: contents`: o protótipo estila
                    // `.cx-stage-chips > svg`, que exige o seta como filha direta.
                    <Fragment key={row.key}>
                      <span className="cx-stage-chip" style={{ "--cx-tone": stageTone(index) } as React.CSSProperties}>
                        <i>{index + 1}</i>
                        {row.name.trim()}
                      </span>
                      {index < list.length - 1 && <ChevronRight aria-hidden="true" />}
                    </Fragment>
                  ))}
              </div>
              <p className="cx-hint">A ordem define a esteira; os blocos do processo atualizam a posição dos cadastros.</p>
            </div>
          </div>
        </div>
        {errors.form && (
          <p className="cx-form-error" role="alert">
            {errors.form}
          </p>
        )}
      </form>
    </Drawer>
  );
}
